// ─────────────────────────────────────────────────────────────
// compressPdf — Tarayıcı içinde PDF küçültme
//
// Amaç: Büyük (yüzlerce MB / GB) katalog PDF'lerini SUNUCUYA GÖNDERMEDEN
// ÖNCE tarayıcıda küçültmek. Böylece ağ üzerinden sadece ~10-30 MB gider;
// büyük dosya yüklemesinde yaşanan kopma (ERR_NETWORK_CHANGED) önlenir.
//
// Yöntem: pdf.js her sayfayı hedef DPI'da canvas'a çizer, jsPDF bu
// görsellerden yeni bir PDF oluşturur (web sitesindeki Flipbook ile aynı
// pdf.js mantığı). pdf.js CDN'den lazy yüklenir (pakete eklenmez).
// ─────────────────────────────────────────────────────────────

import { jsPDF } from 'jspdf';

const PDFJS_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
const PDFJS_WORKER = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

// Bir script'i bir kez yükle
let pdfjsPromise = null;
const loadPdfJs = () => {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (pdfjsPromise) return pdfjsPromise;
  pdfjsPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = PDFJS_SRC;
    s.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
      resolve(window.pdfjsLib);
    };
    s.onerror = reject;
    document.body.appendChild(s);
  });
  return pdfjsPromise;
};

/**
 * Bir PDF File/Blob'unu tarayıcıda küçültür.
 *
 * @param {File|Blob} file  Kaynak PDF
 * @param {object} [opts]
 *   @param {number} [opts.dpi=150]        Hedef çözünürlük (150 = ekranda net)
 *   @param {number} [opts.quality=0.75]   JPEG kalitesi (0-1)
 *   @param {number} [opts.skipBelowMB=20] Bu boyutun altındaki dosyayı küçültme
 *   @param {function} [opts.onProgress]   (done, total) ilerleme bildirimi
 * @returns {Promise<File>}  Küçültülmüş PDF (küçültme atlandıysa orijinal döner)
 */
export const compressPdf = async (file, opts = {}) => {
  const { dpi = 150, quality = 0.75, skipBelowMB = 20, onProgress } = opts;

  // Zaten küçükse dokunma (gereksiz kalite kaybı olmasın)
  if (!file || file.size <= skipBelowMB * 1024 * 1024) return file;

  const pdfjsLib = await loadPdfJs();
  const buf = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
  const total = pdf.numPages;
  const scale = dpi / 72; // pdf.js taban birimi 72 DPI

  let out = null;
  for (let i = 1; i <= total; i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    await page.render({ canvasContext: ctx, viewport }).promise;

    const img = canvas.toDataURL('image/jpeg', quality);

    // Sayfayı orijinal nokta (pt) ölçüsüyle ekle → boyutlar korunur
    const pageW = viewport.width / scale;
    const pageH = viewport.height / scale;
    const orientation = pageW > pageH ? 'l' : 'p';

    if (i === 1) {
      out = new jsPDF({ orientation, unit: 'pt', format: [pageW, pageH], compress: true });
    } else {
      out.addPage([pageW, pageH], orientation);
    }
    out.addImage(img, 'JPEG', 0, 0, pageW, pageH, undefined, 'FAST');

    // Belleği boşalt
    canvas.width = 0;
    canvas.height = 0;
    if (onProgress) onProgress(i, total);
  }

  const blob = out.output('blob');

  // Küçültme beklenenden kötü sonuç verdiyse (büyüdüyse) orijinali kullan
  if (blob.size >= file.size) return file;

  const name = (file.name || 'katalog.pdf').replace(/\.pdf$/i, '') + '_web.pdf';
  return new File([blob], name, { type: 'application/pdf' });
};
